import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export interface SourceLayerOption {
  id: string;
  name: string;
}

export interface SourceLayerSelectProps {
  /** id of the trigger; the label's `htmlFor` points at it. */
  id: string;
  /** data-testid of the trigger; the empty message gets `${testId}-empty`. */
  testId: string;
  label: ReactNode;
  layers: readonly SourceLayerOption[];
  /** '' = nothing picked (shows the placeholder). */
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  help?: ReactNode;
  /** Shown instead of the select when `layers` is empty. Defaults to the
   *  shared "no point cloud" sentence. */
  emptyMessage?: ReactNode;
}

/**
 * D10: every analysis input is a picker pre-filled from context. One control
 * for all of them so the label/trigger wiring, the empty state and the
 * long-name truncation are the same in every dialog.
 *
 * Deliberately not an RHF `FormField`: the dialogs keep the source in
 * component state (`useSourceLayerPick`) and pin it into the mutation
 * variables at submit, so it never takes part in zod validation.
 */
export function SourceLayerSelect({
  id,
  testId,
  label,
  layers,
  value,
  onChange,
  disabled,
  help,
  emptyMessage,
}: SourceLayerSelectProps) {
  const { t } = useTranslation('project');
  return (
    <div className='flex min-w-0 flex-col gap-1.5'>
      <Label htmlFor={id}>{label}</Label>
      {layers.length === 0 ? (
        <p data-testid={`${testId}-empty`} className='text-sm text-slate-600 dark:text-stone-400'>
          {emptyMessage ?? t('analysisLauncher.picker.noPointclouds')}
        </p>
      ) : (
        <Select value={value} onValueChange={onChange} disabled={disabled}>
          <SelectTrigger id={id} data-testid={testId} className='w-full min-w-0'>
            <SelectValue placeholder={t('analysisLauncher.picker.placeholder')} />
          </SelectTrigger>
          <SelectContent>
            {layers.map((l) => (
              <SelectItem key={l.id} value={l.id}>
                {l.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {help && <p className='text-xs text-slate-600 dark:text-stone-400'>{help}</p>}
    </div>
  );
}
